import {FC, useEffect, useState, useCallback, useRef} from 'react';
import {Box, IconButton, Paper, Popper, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ReactComponent as CloseIcon} from '../../../../assets/images/close-base-icon.svg';
import './DocListFilterSelectionModal.scss';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrDatePicker from '../../../common/arr-date-picker/ArrDatePicker';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import type {
  ActiveFilter,
  filterSelectionType,
} from '../doc-list-filter-accordion/DocListFilterAccordion';
import {useGetDocListLoadmetaDataFilters} from '../../../../hooks/document/useDocument';
import {t} from 'i18next';
import LazyLoadFilterList from '../../../common/arr-filter-panel/filter-selection-modal/LazyLoadFilterList';

export const FILTER_SELECTION_MAP: {[key: string]: string} = {
  between: 'Between',
  before: 'Before',
  after: 'After',
  on: 'On',
  include: 'Include',
  exclude: 'Exclude',
  below: 'Below',
  over: 'Over',
  at: 'At',
};

interface FilterSelectionModalProps {
  filterSelectionData: ActiveFilter;
  anchorElement: HTMLElement | null;
  onAddToFilters: (filterData: ActiveFilter) => void;
  onClose: () => void;
  closeOnEsc?: boolean;
}
const DocListFilterSelectionModal: FC<FilterSelectionModalProps> = ({
  filterSelectionData,
  anchorElement,
  onAddToFilters,
  onClose,
  closeOnEsc,
}) => {
  const [filterData, setFilterData] = useState<ActiveFilter>(
    {} as ActiveFilter
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [queryParams, setQueryParams] = useState('');
  // Build query params based on filterData/docListId/fieldId as needed
  useEffect(() => {
    // Example: you may want to use filterData.id as FieldId
    if (filterData?.id) {
      setQueryParams(`?FieldId=${filterData.id}`);
    }
  }, [filterData?.id]);
  const {data, isLoading, isError} =
    useGetDocListLoadmetaDataFilters(queryParams);
  const [isAddToFiltersDisabled, setIsAddToFiltersDisabled] = useState(true);

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || event.shiftKey) return;
    const root = modalRef.current;
    const closeBtn = closeButtonRef.current;
    if (!root || !closeBtn) return;

    // focusable selectors excluding disabled elements
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter((el) => el.offsetParent !== null);
    if (nodes.length === 0) {
      event.preventDefault();
      closeBtn.focus();
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    const idx = active ? nodes.indexOf(active) : -1;

    if (idx === nodes.length - 1) {
      event.preventDefault();
      closeBtn.focus();
    }
  };

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEsc) {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc, true);

    return () => {
      window.removeEventListener('keydown', handleEsc, true);
    };
  }, [onClose, closeOnEsc]);

  useEffect(() => {
    if (filterData.type == 'NVARCHAR' && !filterData.values?.length) {
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'DATETIME' &&
      filterData.selectionType != 'between' &&
      !filterData.startDate
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'DATETIME' &&
      filterData.selectionType == 'between' &&
      (!filterData.startDate || !filterData.endDate)
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (filterData.type == 'INT' && !filterData.selectionType) {
      // Disable if no operator selected for file size
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'INT' &&
      filterData.selectionType &&
      filterData.selectionType != 'between' &&
      !filterData.startSize
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'INT' &&
      filterData.selectionType == 'between' &&
      (!filterData.startSize || !filterData.endSize)
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (Object.keys(filterData).length) {
      setIsAddToFiltersDisabled(false);
    }
  }, [filterData]);
  useEffect(() => {
    const incomingFilterData = {...filterSelectionData};
    if (
      filterSelectionData.type == 'NVARCHAR' &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'include';
    }
    if (
      filterSelectionData.type == 'DATETIME' &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'between';
    }
    // For INT type, don't set default selectionType - let user select first
    setFilterData(incomingFilterData);
  }, [filterSelectionData]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Esc key - Close modal
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      // Enter or Space on Add to Filters button
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        event.target === event.currentTarget
      ) {
        event.preventDefault();
        if (!isAddToFiltersDisabled) {
          onAddToFilters(filterData);
        }
        return;
      }

      // Ctrl + Arrow keys for faster table navigation (optional shortcut)
      if (event.ctrlKey && event.key.startsWith('Arrow')) {
        // This would be handled at parent level for table navigation
        return;
      }
    },
    [onClose, onAddToFilters, filterData, isAddToFiltersDisabled]
  );

  // Focus management - focus close button when modal opens
  useEffect(() => {
    if (anchorElement && closeButtonRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [anchorElement]);

  // Focus trap - keep focus within modal
  useEffect(() => {
    if (!anchorElement || !modalRef.current) return;

    // Override MUI RadioGroup tabindex behavior to make all radio buttons tabbable
    const radioButtons = modalRef.current.querySelectorAll('.MuiRadio-root');
    radioButtons.forEach((radio) => {
      (radio as HTMLElement).setAttribute('tabindex', '-1');
    });

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      // If Shift+Tab on first element, go to last
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // If Tab on last element, go to first
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [anchorElement, filterData]);

  // Handle form submission with Enter key
  const handleFormKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        // Check if target is not a textarea or multiline input
        const target = event.target as HTMLElement;
        if (target.tagName !== 'TEXTAREA' && !isAddToFiltersDisabled) {
          event.preventDefault();
          onAddToFilters(filterData);
        }
      }
    },
    [onAddToFilters, filterData, isAddToFiltersDisabled]
  );
  const radioButtonContainer = (
    <Box
      className="radio-button-container"
      role="radiogroup"
      aria-label={t('docExplorer.filterAccordion.filterOperator')}
    >
      <Box>
        <ArrRadio
          dataTestId="doc-list-include-radio-button"
          aria-label={t('docExplorer.filterAccordion.include')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('docExplorer.filterAccordion.include'),
              value: 'include',
            },
          ]}
          value={filterData.selectionType ?? ''}
          onChange={() => {
            setFilterData((prev: any) => {
              return {...prev, selectionType: 'include'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
      <Box>
        <ArrRadio
          dataTestId="doc-list-exclude-radio-button"
          aria-label={t('docExplorer.filterAccordion.exclude')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('docExplorer.filterAccordion.exclude'),
              value: 'exclude',
            },
          ]}
          value={filterData.selectionType ?? ''}
          onChange={() => {
            setFilterData((prev: any) => {
              return {...prev, selectionType: 'exclude'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
    </Box>
  );
  let multiSelectDropdownContent;
  if (isLoading) {
    multiSelectDropdownContent = (
      <Typography
        role="output"
        aria-live="polite"
        data-testid={`doc-list-filter-selection-modal-loading`}
      >
        {t('common.loading')}
      </Typography>
    );
  } else if (isError) {
    multiSelectDropdownContent = (
      <Typography
        color="error"
        role="alert"
        aria-live="assertive"
        data-testid={`doc-list-filter-selection-modal-error`}
      >
        {t('common.error')}
      </Typography>
    );
  } else {
    multiSelectDropdownContent = (
      <LazyLoadFilterList
        setFilterData={setFilterData}
        filterData={filterData}
        id={filterSelectionData?.id}
        useDataTestIdsSimilarToArrMultiSelect
        checkboxDataTestId="doc-list-filter-selection-modal-multi-select-dropdown-checkbox"
      />
    );
  }
  const multiSelectFilterContainer = <Box>{multiSelectDropdownContent}</Box>;
  const singleSelectDateSelectionTypeContainer = (
    <Box>
      <ArrSingleSelectDropdown
        dataTestId="asd-doc-list-single-select-filter-dropdown"
        aria-label={t('docExplorer.filterAccordion.dateRange')}
        dropdownLabel={t('docExplorer.filterAccordion.dateRange')}
        dropdownItemKey="value"
        dropdownItemDisplay="label"
        dropdownItems={[
          {value: 'between', label: t('docExplorer.filterAccordion.between')},
          {value: 'before', label: t('docExplorer.filterAccordion.before')},
          {value: 'after', label: t('docExplorer.filterAccordion.after')},
          {value: 'on', label: t('docExplorer.filterAccordion.on')},
        ]}
        selectedValues={{
          value: filterData.selectionType,
          label: FILTER_SELECTION_MAP[filterData.selectionType ?? 'between'],
        }}
        onSelectionChange={(value) => {
          setFilterData((prev: any) => {
            return {
              ...prev,
              selectionType: value?.value as filterSelectionType,
              startDate: '',
              endDate: '',
            };
          });
        }}
        searchable
        tabIndex={-1}
      />
    </Box>
  );
  const singleDateContainer = (
    <Box>
      <ArrDatePicker
        dataTestId={`date-picker-filter-${filterData.selectionType}`}
        aria-label={t('docExplorer.filterAccordion.startDate')}
        type="date-picker"
        label={t('docExplorer.filterAccordion.startDate')}
        initialStartDate={filterData.startDate}
        placeholder={t('docExplorer.filterAccordion.datePlaceholder')}
        onChange={(date) => {
          setFilterData((prev: any) => {
            return {...prev, startDate: date as string};
          });
        }}
        showToday={true}
        defaultError={false}
      />
    </Box>
  );
  const multiDateContainer = (
    <Box className="multi-date-container">
      <Box>
        <ArrDatePicker
          dataTestId={`start-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('docExplorer.filterAccordion.startDate')}
          type="date-picker"
          label={t('docExplorer.filterAccordion.startDate')}
          initialStartDate={filterData.startDate}
          placeholder="YYYY/MM/DD"
          onChange={(date) => {
            setFilterData((prev: any) => {
              return {...prev, startDate: date as string};
            });
          }}
          showToday={true}
          defaultError={false}
        />
      </Box>
      <Box>
        <ArrDatePicker
          dataTestId={`end-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('docExplorer.filterAccordion.endDate')}
          type="date-picker"
          label={t('docExplorer.filterAccordion.endDate')}
          initialStartDate={filterData.endDate}
          placeholder="YYYY/MM/DD"
          onChange={(date) => {
            setFilterData((prev: any) => {
              return {...prev, endDate: date as string};
            });
          }}
          showToday={true}
          defaultError={false}
        />
      </Box>
    </Box>
  );
  const singleSelectFileSizeSelectionTypeContainer = (
    <Box>
      <ArrSingleSelectDropdown
        dataTestId="asd-doc-list-file-size-select-filter-dropdown"
        aria-label={t('docExplorer.filterAccordion.setFileSizeRange')}
        dropdownLabel={t('docExplorer.filterAccordion.setFileSizeRange')}
        dropdownItemKey="value"
        dropdownItemDisplay="label"
        dropdownItems={[
          {value: 'between', label: t('docExplorer.filterAccordion.between')},
          {value: 'below', label: t('docExplorer.filterAccordion.below')},
          {value: 'over', label: t('docExplorer.filterAccordion.over')},
          {value: 'at', label: t('docExplorer.filterAccordion.at')},
        ]}
        selectedValues={
          filterData.selectionType
            ? {
                value: filterData.selectionType,
                label: FILTER_SELECTION_MAP[filterData.selectionType],
              }
            : null
        }
        onSelectionChange={(value) => {
          setFilterData((prev: any) => {
            return {
              ...prev,
              selectionType: value?.value as any,
              startSize: '',
              endSize: '',
            };
          });
        }}
        sizeVariant="large"
        searchable
        tabIndex={-1}
      />
    </Box>
  );
  const singleFileSizeContainer = (
    <Box>
      <ArrTextField
        dataTestId="doc-list-file-size-input-filter"
        aria-label={t('docExplorer.filterAccordion.fileSizeValue')}
        placeholder={t('docExplorer.filterAccordion.fromFileSize')}
        value={filterData.startSize || ''}
        onChange={(e: any) => {
          setFilterData((prev: any) => {
            return {...prev, startSize: e.target.value};
          });
        }}
        sizeVariant="medium"
      />
    </Box>
  );
  const multiFileSizeContainer = (
    <Box className="multi-size-container">
      <Box>
        <ArrTextField
          dataTestId="doc-list-start-file-size-input-filter"
          aria-label={t('docExplorer.filterAccordion.startSize')}
          // label={t('docExplorer.filterAccordion.startSize')}
          placeholder={t('docExplorer.filterAccordion.fromFileSize')}
          value={filterData.startSize || ''}
          onChange={(e: any) => {
            setFilterData((prev: any) => {
              return {...prev, startSize: e.target.value};
            });
          }}
          sizeVariant="medium"
        />
      </Box>
      <Box>
        <ArrTextField
          dataTestId="doc-list-end-file-size-input-filter"
          aria-label={t('docExplorer.filterAccordion.endSize')}
          //label={t('docExplorer.filterAccordion.endSize')}
          placeholder={t('docExplorer.filterAccordion.toFileSize')}
          value={filterData.endSize || ''}
          onChange={(e: any) => {
            setFilterData((prev: any) => {
              return {...prev, endSize: e.target.value};
            });
          }}
          sizeVariant="medium"
        />
      </Box>
    </Box>
  );
  return (
    <Popper
      data-testid="doc-list-filter-selection-modal"
      open={Boolean(anchorElement)}
      anchorEl={anchorElement}
      placement="bottom-start"
      className="doc-list-filter-selection-popper"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-modal-title"
      onKeyDown={handleKeyDown}
    >
      <Paper
        className="doc-list-filter-selection-modal"
        ref={modalRef}
        onKeyDown={handleFormKeyDown}
        tabIndex={-1}
      >
        <Box className="modal-header" onKeyDown={handleOnKeyDown}>
          <Typography
            id="filter-modal-title"
            className="typo-text subhead4_size title"
            data-testid={`doc-list-filter-selection-modal-filter-by`}
          >
            {t('docExplorer.filterAccordion.filterBy', {
              label: filterData.label,
            })}
          </Typography>
          {/* Fix: set Typography to span to avoid <p> in <p> */}
          <Typography
            component="span"
            className="typo-text subhead4_size title"
            data-testid={`doc-list-filter-selection-modal-typography`}
          ></Typography>
          <div
            style={{display: 'flex', alignItems: 'center'}}
            data-testid={`doc-list-filter-selection-modal-div`}
          >
            <IconButton
              ref={closeButtonRef}
              data-testid="doc-list-filter-selection-close-button"
              aria-label={t('common.close')}
              size="small"
              onClick={onClose}
              className="modal-close"
              tabIndex={0}
            >
              <CloseIcon className="close-icon" aria-hidden="true" />
            </IconButton>
          </div>
        </Box>
        <fieldset
          className="primary-selection"
          aria-label={t('docExplorer.filterAccordion.filterType')}
          tabIndex={-1}
          data-testid={`doc-list-filter-selection-modal-fieldset-primary`}
        >
          {filterData.type == 'NVARCHAR' && radioButtonContainer}
          {filterData.type == 'DATETIME' &&
            singleSelectDateSelectionTypeContainer}
          {filterData.type == 'INT' &&
            singleSelectFileSizeSelectionTypeContainer}
        </fieldset>
        <fieldset
          className="secondery-selection"
          aria-label={t('docExplorer.filterAccordion.filterValues')}
          tabIndex={-1}
          data-testid={`doc-list-filter-selection-modal-fieldset-secondary`}
        >
          {(filterData.selectionType == 'include' ||
            filterData.selectionType == 'exclude') &&
            multiSelectFilterContainer}

          {(filterData.selectionType == 'before' ||
            filterData.selectionType == 'after' ||
            filterData.selectionType == 'on') &&
            singleDateContainer}

          {filterData.type == 'DATETIME' &&
            filterData.selectionType == 'between' &&
            multiDateContainer}

          {(filterData.selectionType == 'below' ||
            filterData.selectionType == 'over' ||
            filterData.selectionType == 'at') &&
            singleFileSizeContainer}

          {filterData.type == 'INT' &&
            filterData.selectionType == 'between' &&
            multiFileSizeContainer}
        </fieldset>
        <Box className="modal-submit">
          <ArrButton
            ref={submitButtonRef}
            dataTestId="doc-list-filter-selection-modal-submit"
            aria-label={t('docExplorer.filterAccordion.addToFilters')}
            size="small"
            disabled={isAddToFiltersDisabled}
            isAriaDisabled={isAddToFiltersDisabled}
            onClick={() => {
              onAddToFilters(filterData);
            }}
            tabIndex={0}
          >
            {t('docExplorer.filterAccordion.addToFilters')}
          </ArrButton>
        </Box>
      </Paper>
    </Popper>
  );
};

export default DocListFilterSelectionModal;
